import React, { useState } from "react";
import {
  Grid,
  GridColumn as Column,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridCustomCellProps,
  GridDataStateChangeEvent,
} from "@progress/kendo-react-grid";
import { SelectDescriptor } from "@progress/kendo-react-data-tools";
import { SvgIcon } from "@progress/kendo-react-common";
import { circleIcon } from "@progress/kendo-svg-icons";
import { Button } from "@progress/kendo-react-buttons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faQuestion } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb } from "@progress/kendo-react-layout";
import SelectedAssetMetricsReadDto, {
  EndpointDetailsDto,
} from "../../../../../../Models/Dashboard/SelectedAssetMetricsReadDto";
import { GetEndpointIcon, GetBulkFlowIcon } from "../../../../../../Utilities/CustomIcons";
import { GetEndpointMP2Icon } from "../../../../../../Utilities/CustomIcons";
import { DeviceType } from "../../../../../../Enums/DeviceType";
import { formatSmartDate } from "../../../../../Shared/Utilities/DateFormattingUtils";
import { enMessages } from "../../../../../../messages/en-US";
import { useLocalization } from "@progress/kendo-react-intl";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { DataResult, State, process } from "@progress/kendo-data-query";
import EntityNotesButton from "../../Notes/EntityNotesButton";

interface Props {
  assetData: SelectedAssetMetricsReadDto;
  selectedGroupId: number | null;
  selectedEndpointId: string | null;
  onEndpointSelect: (endpoint: EndpointDetailsDto | null) => void;
  onBack?: () => void;
}

// Connectivity rank by criticality (most critical first) — drives the default order and column sorting
const connectivityOrder = { 'Offline': 0, 'Partial': 1, 'Online': 2, 'Unknown': 3 };

const ComplianceCell = (props: GridCustomCellProps) => {
  const localizationService = useLocalization();
  const percentage = props.dataItem.compliance.percentage < 0 ? '-' : props.dataItem.compliance.percentage.toFixed(0) + '%';

  // Get the tooltip text based on compliance status
  let tooltipText = "";
  if (props.dataItem.compliance.status === "Compliant") {
    tooltipText = localizationService.toLanguageString(
      "custom.compliant",
      enMessages.custom.compliant
    );
  } else if (props.dataItem.compliance.status === "NonCompliant") {
    tooltipText = localizationService.toLanguageString(
      "custom.nonCompliant",
      enMessages.custom.nonCompliant
    );
  } else if (props.dataItem.compliance.status === "PartiallyCompliant") {
    tooltipText = localizationService.toLanguageString(
      "custom.partiallyCompliant",
      enMessages.custom.partiallyCompliant
    );
  } else {
    tooltipText = props.dataItem.compliance.status || localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown);
  }

  return (
    <td>
      <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            cursor: "pointer",
          }}
          title={tooltipText}
        >
          <SvgIcon
            icon={circleIcon}
            size="large"
            color={
              props.dataItem.compliance.status === "Compliant"
                ? "#3FA43F"
                : props.dataItem.compliance.status === "NonCompliant"
                ? "#FF3630"
                : props.dataItem.compliance.status === "PartiallyCompliant"
                ? "#FFBF00"
                : "#85868E"
            }
          />
          <span>{percentage}</span>
        </span>
      </Tooltip>
    </td>
  );
};

const ConnectivityCell = (props: GridCustomCellProps) => {
  const localizationService = useLocalization();

  // Get the tooltip text based on connectivity status
  let tooltipText = "";
  if (props.dataItem.connectivity === "Online") {
    tooltipText = localizationService.toLanguageString(
      "custom.online",
      enMessages.custom.online
    );
  } else if (props.dataItem.connectivity === "Offline") {
    tooltipText = localizationService.toLanguageString(
      "custom.offline",
      enMessages.custom.offline
    );
  } else if (props.dataItem.connectivity === "Partial") {
    tooltipText = localizationService.toLanguageString(
      "custom.partialConnected",
      enMessages.custom.partialConnected
    );
  } else {
    tooltipText =
      props.dataItem.connectivity === "Unknown"
        ? localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown)
        : props.dataItem.connectivity;
  }

  return (
    <td>
      <SvgIcon
            icon={circleIcon}
            size="large"
            color={
              props.dataItem.connectivity === "Online"
                ? "#3FA43F"
                : props.dataItem.connectivity === "Offline"
                ? "#FF3630"
                : props.dataItem.connectivity === "Partial"
                ? "#FFBF00"
                : "#85868E"
            }
          />
          <span>{tooltipText}</span>
    </td>
  );
};

const EmptyHeaderCell = () => <th></th>;

const EndpointTableComponent: React.FC<Props> = ({
  assetData,
  selectedGroupId,
  selectedEndpointId,
  onEndpointSelect,
  onBack,
}) => {
  const localizationService = useLocalization();

  const filteredEndpointsData = React.useMemo(() => {
    // First check if we have the data structure
    if (!assetData?.assetConfigurationDetails?.endpoints) {
      return [];
    }

    const result =
      selectedGroupId === 0
        ? assetData.assetConfigurationDetails.endpoints
        : selectedGroupId === null
        ? []
        : assetData.assetConfigurationDetails.endpoints.filter(
            (endpoint: EndpointDetailsDto) => {
              // Type guard for selectedGroupId
              if (typeof selectedGroupId !== "number") return false;
              const groupIdStr = selectedGroupId.toString();
              return endpoint.groupId === groupIdStr;
            }
          );

    return result
      .map((endpoint: EndpointDetailsDto) => ({
        ...endpoint,
        connectivityRank: connectivityOrder[endpoint.connectivity] ?? 3,
      }))
      // Default order: connectivity first (most critical), then compliance
      .sort((a, b) => a.connectivityRank - b.connectivityRank || a.compliance.percentage - b.compliance.percentage);
  }, [selectedGroupId, assetData]);

  // Get selected group name
  const selectedGroupName =
    assetData?.assetConfigurationDetails?.groups?.find(
      (group: any) => group.id?.toString() === selectedGroupId?.toString()
    )?.name || "";

  // Initialize selection with the first item if filteredEndpoints array is not empty
  const [select, setSelect] = React.useState<SelectDescriptor>({});

  // Update selection when filtered data changes, preserving selection from parent
  React.useEffect(() => {
    if (filteredEndpointsData.length > 0) {
      let endpointToSelect: EndpointDetailsDto;

      // Try to find the endpoint specified by parent
      const parentSelectedEndpoint = selectedEndpointId
        ? filteredEndpointsData.find(ep => ep.id.toString() === selectedEndpointId)
        : null;

      if (parentSelectedEndpoint) {
        // Use the endpoint specified by parent
        endpointToSelect = parentSelectedEndpoint;
      } else {
        // Fall back to the first endpoint (data comes pre-sorted by default order)
        endpointToSelect = filteredEndpointsData[0];
      }

      setSelect({
        [endpointToSelect.id.toString()]: true,
      });

      // Emit the selected endpoint
      onEndpointSelect?.(endpointToSelect);
    } else {
      setSelect({});
      // Emit null when no endpoints
      onEndpointSelect?.(null);
    }
  }, [filteredEndpointsData, selectedEndpointId, onEndpointSelect]);

  const onSelectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelection: SelectDescriptor = {};
      const selectedKey = Object.keys(event.select)[0];
      if (selectedKey) {
        newSelection[selectedKey] = true;
        // Find and emit the selected endpoint
        const selectedEndpoint = filteredEndpointsData.find(
          (endpoint) => endpoint.id.toString() === selectedKey
        );
        onEndpointSelect?.(selectedEndpoint || null);
      }
      setSelect(newSelection);
    },
    [filteredEndpointsData, onEndpointSelect]
  );

  const onHeaderSelectionChange = React.useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      if (event.select[0] && filteredEndpointsData.length > 0) {
        setSelect({ [filteredEndpointsData[0].id.toString()]: true });
      } else {
        setSelect({});
      }
    },
    [filteredEndpointsData]
  );

  const machineryType = (assetData as any)?.machineryType;
  const isLocomotive = machineryType === "Locomotive";

  const LastGreaseEventCell = (props: GridCustomCellProps) => {
    const localizationService = useLocalization();
    const { displayText: lastGreaseEventText, tooltipText } = formatSmartDate(
      props.dataItem.lastGreaseEvent,
      localizationService,
      enMessages
    );

    return <td>
      <Tooltip 
        anchorElement="target" 
        position="bottom"
        parentTitle={true}
        content={() => tooltipText}
      >
        <span style={{ cursor: 'pointer' }} title=" ">
          {lastGreaseEventText}
        </span>
      </Tooltip>
    </td>;
  };

  const LastGreaseEventHeaderCell = (props) => {
    const tooltipText = isLocomotive
      ? localizationService.toLanguageString('custom.lastFuelEventTooltip', enMessages.custom.lastFuelEventTooltip)
      : localizationService.toLanguageString('custom.lastGreaseEventTooltip', enMessages.custom.lastGreaseEventTooltip);
    
    return (
      <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
        <span className="k-cell-inner">
          <span className="k-link" onClick={props.onClick}>
            <span
              className="k-column-title"
              title={tooltipText}
            >
              {props.title}
            </span>
            {props.children}
          </span>
        </span>
      </Tooltip>
    );
  };

  const LastSyncCell = (props: GridCustomCellProps) => {
    const localizationService = useLocalization();
    const { displayText: lastSyncText, tooltipText } = formatSmartDate(
      props.dataItem.lastSync,
      localizationService,
      enMessages
    );

    return <td>
      <Tooltip 
        anchorElement="target" 
        position="bottom"
        parentTitle={true}
        content={() => tooltipText}
      >
        <span style={{ cursor: 'pointer' }} title=" ">
          {lastSyncText}
        </span>
      </Tooltip>
    </td>;
  };

  const LastSyncHeaderCell = (props) => (
    <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
      <span className="k-cell-inner">
        <span className="k-link" onClick={props.onClick}>
          <span className="k-column-title" title={localizationService.toLanguageString('custom.lastSyncTooltip', enMessages.custom.lastSyncTooltip)}>
            {props.title}
          </span>
          {props.children}
        </span>
      </span>
    </Tooltip>
  );

  const LastHeartbeatCell = (props: GridCustomCellProps) => {
    const localizationService = useLocalization();
    const { displayText: lastHeartbeatEventText, tooltipText } = formatSmartDate(
      props.dataItem.lastHeartbeatEvent,
      localizationService,
      enMessages
    );

    return <td>
      <Tooltip 
        anchorElement="target" 
        position="bottom"
        parentTitle={true}
        content={() => tooltipText}
      >
        <span style={{ cursor: 'pointer' }} title=" ">
          {lastHeartbeatEventText}
        </span>
      </Tooltip>
    </td>;
  };

  const LastHeartbeatHeaderCell = (props) => (
    <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
      <span className="k-cell-inner">
        <span className="k-link" onClick={props.onClick}>
          <span
            className="k-column-title"
              title={localizationService.toLanguageString('custom.lastHeartbeatTooltip', enMessages.custom.lastHeartbeatTooltip)}
            >
            {props.title}
          </span>
          {props.children}
        </span>
      </span>
    </Tooltip>
  );

  const ComplianceHeaderCell = (props) => {
    const TooltipContent = () => (
      <div className="p-2">
        <div>{localizationService.toLanguageString('custom.complianceTooltip', enMessages.custom.complianceTooltip)}</div>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="small" color="#FF3630" />
          <span>{'≤50%'}</span>
        </div>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="small" color="#FFBF00" />
          <span>{'>50% <80%'}</span>
        </div>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="small" color="#3FA43F" />
          <span>{'≥80%'}</span>
        </div>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="small" color="#85868E" />
          <span>{localizationService.toLanguageString("custom.unknown", enMessages.custom.unknown)}</span>
        </div>
      </div>
    );

    return (
      <span className="k-cell-inner">
        <span className="k-link" onClick={props.onClick}>
          <Tooltip
            anchorElement="target"
            position="bottom"
            parentTitle={true}
            content={TooltipContent}
          >
            <span className="k-column-title" style={{ cursor: 'pointer' }} title=" ">
              {props.title}
            </span>
          </Tooltip>
          {props.children}
        </span>
      </span>
    );
  };

  const ConnectivityHeaderCell = (props) => {
    const TooltipContent = () => (
      <div className="p-2">
        <div>{localizationService.toLanguageString('custom.connectivityTooltip', enMessages.custom.connectivityTooltip)}</div>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="small" color="#3FA43F" />
          <span>{localizationService.toLanguageString("custom.online", enMessages.custom.online)}</span>
        </div>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="small" color="#FF3630" />
          <span>{localizationService.toLanguageString("custom.offline", enMessages.custom.offline)}</span>
        </div>
      </div>
    );

    return (
      <span className="k-cell-inner">
        <span className="k-link" onClick={props.onClick}>
          <Tooltip
            anchorElement="target"
            position="bottom"
            parentTitle={true}
            content={TooltipContent}
          >
            <span className="k-column-title" style={{ cursor: 'pointer' }} title=" ">
              {props.title}
            </span>
          </Tooltip>
          {props.children}
        </span>
      </span>
    );
  };

  const TypeCell = (props: GridCustomCellProps) => {
    let tooltipText = "";
    if (props.dataItem.entityType === DeviceType.EndpointMP2) {
      tooltipText = `Endpoint MP2 - ${props.dataItem.serialNumber}`;
    } else if (props.dataItem.entityType === DeviceType.EndpointLF) {
      tooltipText = `Endpoint LF - ${props.dataItem.serialNumber}`;
    } else if (props.dataItem.entityType === DeviceType.EndpointHF) {
      tooltipText = `Endpoint HF - ${props.dataItem.serialNumber}`;
    } else if (props.dataItem.entityType === "Endpoint") {
      tooltipText = `Endpoint - ${props.dataItem.serialNumber}`;
    } else if (props.dataItem.entityType === DeviceType.EndpointBF) {
      tooltipText = `Endpoint BF - ${props.dataItem.serialNumber}`;
    } else {
      tooltipText = localizationService.toLanguageString('custom.unknownType', enMessages.custom.unknownType) + " - " + localizationService.toLanguageString('custom.noSerialNumber', enMessages.custom.noSerialNumber);
    }
    return (
      <td style={{padding: "0px"}}>
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              cursor: "default",
            }}
            title={tooltipText}
          >
            {props.dataItem.entityType === DeviceType.EndpointMP2 ? (
              GetEndpointMP2Icon(20, 20)
            ) : props.dataItem.entityType === DeviceType.EndpointLF ? (
              GetEndpointIcon(22, 22)
            ) : props.dataItem.entityType === DeviceType.EndpointHF ? (
              GetEndpointIcon(22, 22)
            ) : props.dataItem.entityType === "Endpoint" ? (
              GetEndpointIcon(22, 22)
            ) : props.dataItem.entityType === DeviceType.EndpointBF ? (
              GetBulkFlowIcon(22, 22)
            ) : (
              <FontAwesomeIcon icon={faQuestion} size="lg" />
            )}
          </span>
        </Tooltip>
      </td>
    );
  };

  const selectedEndpoint = React.useMemo(() => {
    const selectedKey = Object.keys(select)[0];
    if (!selectedKey) return undefined;
    return filteredEndpointsData.find(
      (endpoint) => endpoint.id.toString() === selectedKey
    );
  }, [select, filteredEndpointsData]);


  const items = React.useMemo(
    () => [
      { id: "group", text: selectedGroupName },
      {
        id: "endpoint",
        text: `${selectedEndpoint?.name ?? ""} (${
          selectedEndpoint?.serialNumber
            ? selectedEndpoint.serialNumber
            : localizationService.toLanguageString('custom.noSerialNumber', enMessages.custom.noSerialNumber)
        })`,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedGroupName, selectedEndpoint]
  );

    //Creating dataState to prevent issues with virtual scrolling
    const createDataState = (dataState: State) => {
      // Add selection information to the data before processing
      const dataWithSelection = filteredEndpointsData.map(endpoint => ({
        ...endpoint,
        selected: select[endpoint.id.toString()] || false
      }));
      
      return {
        result: process(dataWithSelection.slice(0), dataState),
        dataState: dataState,
      };
    };
  
    let initialState = createDataState({
      take: 30,
      skip: 0,
    });
  
  
    const [result, setResult] = useState<DataResult>(initialState.result);
    const [dataState, setDataState] = useState<State>(initialState.dataState);
  
  
    const dataStateChange = (event: GridDataStateChangeEvent) => {
      // No sort selected = default order (data comes pre-sorted)
      let updatedState = createDataState(event.dataState);
      setResult(updatedState.result);
      setDataState(updatedState.dataState);
    };

    // Update result data when selection changes
    React.useEffect(() => {
      const updatedState = createDataState(dataState);
      setResult(updatedState.result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [select, filteredEndpointsData, dataState]);

  return (
    <div className="flex flex-col h-full min-h-0 checkbox-outline-soft-override">
      <Tooltip anchorElement="target" position="top">
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-center min-w-0 flex-1">
            <Button onClick={onBack} fillMode="clear">
              <FontAwesomeIcon icon={faArrowLeft} className="" size="lg" />
            </Button>
            <div className="text-xl font-bold truncate">
              <Breadcrumb
                data={items}
                disabled={true}
                className="breadcrumb-override text-lg truncate"
              />
            </div>
          </div>
          {selectedEndpoint && (
            <EntityNotesButton
              entityType="Endpoint"
              entityId={selectedEndpoint.id.toString()}
              entityName={selectedEndpoint.name}
              entitySerial={selectedEndpoint.serialNumber}
            />
          )}
        </div>
      </Tooltip>
      <Grid
        data={result}
        onDataStateChange={dataStateChange}
        {...dataState}
        sortable={{
          mode: "single"
        }}
        style={{ overflow: "auto" }}
        dataItemKey="id"
        selectable={{
          enabled: true,
          drag: false,
          cell: false,
          mode: "single",
        }}
        select={select}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
      >
        <Column
          field="selected"
          width="40px"
          columnType="checkbox"
          cells={{ headerCell: EmptyHeaderCell }}
          sortable={false}
        />
        <Column
          field="entityType"
          title=" "
          cells={{ data: TypeCell }}
          width="45px"
          sortable={false}
        />
        <Column
          field="name"
          title={localizationService.toLanguageString(
            "custom.name",
            enMessages.custom.name
          )}
        />
        <Column
          field="compliance.percentage"
          title={localizationService.toLanguageString(
            "custom.compliance",
            enMessages.custom.compliance
          )}
          cells={{ data: ComplianceCell }}
          headerCell={ComplianceHeaderCell}
        />
        <Column
          field="connectivityRank"
          title={localizationService.toLanguageString(
            "custom.connectivity",
            enMessages.custom.connectivity
          )}
          cells={{ data: ConnectivityCell }}
          headerCell={ConnectivityHeaderCell}
        />
        <Column
          field="lastGreaseEvent"
          title={isLocomotive
            ? localizationService.toLanguageString('custom.lastFuelEvent', enMessages.custom.lastFuelEvent)
            : localizationService.toLanguageString('custom.lastGreaseEvent', enMessages.custom.lastGreaseEvent)}
          cells={{ data: LastGreaseEventCell }}
          headerCell={LastGreaseEventHeaderCell}
        />
        <Column
          field="lastSync"
          title={localizationService.toLanguageString('custom.lastSync', enMessages.custom.lastSync)}
          cells={{ data: LastSyncCell }}
          headerCell={LastSyncHeaderCell}
        />
        {!isLocomotive && (
          <Column
            field="lastHeartbeatEvent"
            title={localizationService.toLanguageString('custom.lastHeartbeat', enMessages.custom.lastHeartbeat)}
            cells={{ data: LastHeartbeatCell }}
            headerCell={LastHeartbeatHeaderCell}
          />
        )}
      </Grid>
    </div>
  );
};

export default EndpointTableComponent;
